import { forwardRef } from 'react'
import type { JSX } from 'react'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'

import { clsx } from 'clsx'

import {
  Card,
  ChannelIcon,
  EllipsisVerticalIcon,
  Flex,
  Separator,
} from '@buffer-mono/popcorn'

import type { Service } from '~publish/components/Composer/graphqlEnums'

import styles from './Common.module.css'

export const PostPreviewCard = forwardRef<
  HTMLDivElement,
  { className?: string; 'data-testid': string; children: ReactNode }
>(({ className, children, ...props }, forwardedRef): JSX.Element => {
  return (
    <Card
      className={clsx(styles.cardWrapper, className)}
      {...props}
      ref={forwardedRef}
    >
      {children}
    </Card>
  )
})

PostPreviewCard.displayName = 'PostPreviewCard'

export const PostWrapper = ({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<'div'>): JSX.Element => {
  return (
    <div className={clsx(styles.postWrapper, className)} {...props}>
      {children}
    </div>
  )
}

export const DotsIcon = ({
  color,
  className,
  style,
  ...props
}: ComponentPropsWithoutRef<typeof EllipsisVerticalIcon> & {
  color?: string
}): JSX.Element => {
  return (
    <EllipsisVerticalIcon
      className={clsx(styles.dotsIcon, className)}
      style={{ ...(color ? { color } : null), ...style }}
      {...props}
    />
  )
}

export const DotsIconSmall = ({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof DotsIcon>): JSX.Element => {
  return (
    <DotsIcon className={clsx(styles.dotsIconSmall, className)} {...props} />
  )
}

export const AvatarSquare = ({
  src,
  fallbackUrl,
  alt = '',
  className,
  style,
  ...props
}: Omit<ComponentPropsWithoutRef<'img'>, 'src'> & {
  src: string
  fallbackUrl: string
}): JSX.Element => {
  return (
    <img
      src={src}
      alt={alt}
      className={clsx(styles.avatarSquare, className)}
      style={{ backgroundImage: `url(${src}), url(${fallbackUrl})`, ...style }}
      {...props}
    />
  )
}

export const EmptyMediaPlaceholder = ({
  service,
  className,
}: {
  service: Service
  className?: string
}): JSX.Element => {
  return (
    <Flex
      align="center"
      justify="center"
      fullWidth
      fullHeight
      className={clsx(styles.emptyMediaPlaceholder, className)}
    >
      <ChannelIcon type={service} color="inverted" size="large" />
    </Flex>
  )
}

export const ThreadsConnector = (): React.JSX.Element => {
  return (
    <Separator
      orientation="vertical"
      decorative={true}
      className={styles.threadsConnector}
    />
  )
}

/**
 * Mirrors each network's native "made with AI" label inside the post preview.
 * Visibility is decided upstream where the real Draft is available (see
 * canDiscloseAiContent); this only renders the marker. The `overlay` variant is
 * a dark pill for use over media/video (dark in every theme, e.g. X's on-image
 * label); the `inline` variant is plain secondary-color text for use on the
 * post body (e.g. X's label under a text-only tweet). The visible label is the
 * accessible name.
 */
export const PreviewAiMarker = ({
  icon,
  label,
  className,
  variant = 'overlay',
}: {
  icon?: ReactNode
  label: string
  className?: string
  variant?: 'overlay' | 'inline'
}): JSX.Element => {
  return (
    <div
      className={clsx(
        variant === 'inline' ? styles.aiMarkerInline : styles.aiMarkerOverlay,
        className,
      )}
      data-testid="ai-marker"
    >
      {icon}
      {label}
    </div>
  )
}
